import { useState, useEffect } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useNavigate } from "react-router-dom";
import { loginRequest } from "../config/authConfig";
import axios from "../api/axios";
import { C, F, R, T } from "../../../../../CRM/frontend/src/design/tokens";

const MicrosoftLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 21" width="20" height="20" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

// This intentionally mirrors the CRM's real login flow (MSAL login →
// backend token exchange → JWT in storage), not internal.withacg.com's
// client-side-only pattern.
export default function Login() {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState("");
  const [accessRestricted, setAccessRestricted] = useState(false);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    if (!isAuthenticated) {
      setChecking(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        if (!localStorage.getItem("xtm_token")) {
          const accounts = instance.getAllAccounts();
          if (accounts.length === 0) {
            if (!cancelled) setChecking(false);
            return;
          }
          instance.setActiveAccount(accounts[0]);
          const result = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
          const response = await axios.post("/auth/microsoft", { access_token: result.accessToken });
          if (cancelled) return;
          localStorage.setItem("xtm_token", response.data.access_token);
        }
        if (cancelled) return;
        navigate("/overview", { replace: true });
      } catch (err) {
        if (cancelled) return;
        setChecking(false);
        if (err?.response?.status === 403) {
          setAccessRestricted(true);
        } else {
          setError("Auto sign-in failed. Click below to sign in manually.");
        }
      }
    })();

    return () => { cancelled = true; };
  }, [instance, navigate, isAuthenticated, inProgress]);

  const handleSignIn = async () => {
    setLoading(true);
    setError("");
    setAccessRestricted(false);
    try {
      localStorage.removeItem("xtm_token");
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      setError(`Sign-in failed: ${err?.errorMessage || err?.message || String(err)}`);
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.surfaceBase }}>
        <span style={{
          width: 20, height: 20, border: `2px solid ${C.border}`, borderTopColor: C.textSecondary,
          borderRadius: "50%", display: "inline-block", animation: "xtmSpin 0.8s linear infinite",
        }} />
        <style>{`@keyframes xtmSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (accessRestricted) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", gap: 16, alignItems: "center", justifyContent: "center", background: "#0f1b2d", fontFamily: F.family }}>
        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: 15, fontWeight: 600, margin: 0 }}>Access Restricted</p>
        <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, margin: 0 }}>Your account is not authorized for XTM.</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: C.surfaceBase, fontFamily: F.family }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 320 }}>
        <div style={{ fontSize: F.h1, fontWeight: F.semibold, color: C.textPrimary, marginBottom: 32 }}>XTM sign in</div>
        <button
          onClick={handleSignIn}
          disabled={loading}
          style={{
            width: "100%", height: 36, display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
            border: "none", borderRadius: R.md, background: C.primary, color: C.white,
            fontSize: F.md, fontWeight: F.semibold, fontFamily: F.family,
            cursor: loading ? "not-allowed" : "pointer", transition: T.base, opacity: loading ? 0.6 : 1,
          }}
        >
          <MicrosoftLogo />
          {loading ? "Signing in…" : "Sign in with Microsoft"}
        </button>
        {error && <div style={{ marginTop: 20, fontSize: F.sm, color: C.danger, textAlign: "center" }}>{error}</div>}
      </div>
    </div>
  );
}
